import { useState, useMemo, useEffect } from 'react';
import { CustomButton } from 'shared_ui_components';
import useGetSessionQuery from 'features/realplays/services/queries/useGetSession';
import useScenarioUpdateMutation from 'features/realplays/services/mutations/useScenarioUpdate';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import useSubmitRolePlayMutation from 'features/realplays/services/mutations/useSubmitRoleplay';
import { TooltipCustom } from 'utils';
import { Button } from '@material-ui/core';
import RealPlayRecordDataHelper from 'features/realplays/RealPlayRecordData.helper';

const RealPlaySubmit = () => {
  const history = useHistory();
  let location = useLocation();
  const { uuid } = useParams();

  const [enabled, setEnabled] = useState(false);
  const {
    isLoadingVideoTemp,
    assignment_uuid,
    roleplay,
    scenarios,
    selectedScenario: scenarioData,
    session_uuid,
    status,
    tempVideoUrl: recordingURL,
  } = RealPlayRecordDataHelper();

  const id = useMemo(() => scenarioData?.uuid, [scenarioData]);

  const allHaveVideos = useMemo(() => {
    if (!scenarios || scenarios?.length === 0) return false;
    return scenarios?.every((s) => {
      const response = s?.response_retake || s?.response;
      const video = response?.video;
      return video !== null && video !== '';
    });
  }, [scenarios]);

  const scenarios_response = useMemo(() => {
    if (!scenarios || scenarios?.length === 0) return false;
    //TODO: add response retaken.
    return scenarios?.map((sce) => ({
      scenario_uuid: sce?.uuid,
      response_uuid: sce?.response?.uuid,
    }));
  }, [scenarios]);

  const { refetch } = useGetSessionQuery({
    assignment_uuid,
    scenario_uuid: scenarioData?.uuid,
    session_uuid,
    enabled: enabled && !!session_uuid,
  });

  const { mutate: scenarioUpdate, isLoading: isLoadingScenario } = useScenarioUpdateMutation();
  const { mutate: submitRoleplay, isLoading: isLoadingSubmit } = useSubmitRolePlayMutation();

  const isLastScenario = useMemo(() => {
    if (scenarios?.length === 1) return true;
    const allHaveVideos = scenarios
      ?.filter((s) => s?.uuid !== id)
      ?.map((s) => s?.response_retake || s?.response)
      ?.every((response) => !!response?.video);
    return allHaveVideos;
  }, [scenarios, id]);

  const label = useMemo(() => {
    if (isLoadingScenario || isLoadingSubmit) return 'Loading...';
    if (isLoadingVideoTemp) return 'Loading video...';
    if (isLastScenario) return 'Save and submit';
    return 'Save and continue';
  }, [isLoadingScenario, isLoadingSubmit, isLastScenario, isLoadingVideoTemp]);

  const navigateNext = () => {
    if (isLastScenario) {
      if (scenarios?.length === 1) {
        submitRoleplay(
          { assignment_uuid: uuid, title: roleplay?.name, scenarios_response },
          {
            onSuccess: (response) => {
              history.push({
                pathname: `/roleplay`,
                state: {
                  prevPath: `${location.pathname}${location.search ? location.search : ''}`,
                },
              });
            },
          }
        );
      } else return history.push(`/realplay/${uuid}/submit`);
    }
  };

  const onSubmit = () => {
    if (enabled) {
      refetch();
      return;
    }
    setEnabled(true);
  };

  const handleAfterCheckSession = () => {
    setEnabled(false);
    if (!recordingURL || !id) return;

    const response_uuid = scenarioData?.response?.uuid;

    scenarioUpdate(
      { url_video: recordingURL, response_uuid, session_uuid, roleplay_id: uuid },
      { onSuccess }
    );
  };

  const handleSubmit = () => history.push(`/realplay/${uuid}/submit`);

  const onSuccess = () => {
    navigateNext();
  };

  const isDisabled = useMemo(
    () => isLoadingScenario || recordingURL === '',
    [isLoadingScenario, recordingURL]
  );

  useEffect(() => {
    if (!enabled) return;
    if (status === 'DRAFT') handleAfterCheckSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, status]);

  return allHaveVideos ? (
    <CustomButton
      onClick={handleSubmit}
      className="primaryBtn"
      style={{ width: '100%' }}
      disabled={isLoadingSubmit}
    >
      Submit
    </CustomButton>
  ) : status === 'INCOMPLETE' ? (
    <TooltipCustom
      title={"Your video doesn't have content or interaction, please interact with the coach"}
      arrow
      placement="top"
    >
      <span>
        <Button style={{ width: '100%', pointerEvents: 'visible !important' }} disabled>
          {label}
        </Button>
      </span>
    </TooltipCustom>
  ) : (
    <CustomButton
      onClick={onSubmit}
      className="primaryBtn"
      style={{ width: '100%' }}
      disabled={isDisabled || isLoadingVideoTemp}
    >
      {label}
    </CustomButton>
  );
};

export default RealPlaySubmit;
